import { Alert, Box, Button, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha } from '@mui/material'
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined'
import DownloadOutlined from '@mui/icons-material/DownloadOutlined'
import { FunctionComponent, useContext, useRef, useState } from 'react'
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper'
import { LoadingSpinner } from '../../../presentation-components/display-elements-components/loading-spinner'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import NominalCodeMappingService from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.service'
import {
  INominalCodeImportPreview,
  INominalCodeImportRow,
  IPostNominalCodeMapping,
} from '../../../../shared/services/nominal-code-mapping/nominal-code-mapping.model'
import { downloadExcel } from '../../../../shared/utils/import-utils'

const ACCEPTED_FILE_TYPES = '.xlsx,.xls,.csv'

// The outcome as the user should read it back, not the stored slug. An import can only produce
// 'spend', 'exclude' or 'private', or no outcome at all for a code added to the chart; anything
// else failed validation and shows its errors instead.
const outcomeLabel = (row: INominalCodeImportRow): string => {
  if (row.action === 'account') return 'No outcome yet'
  if (row.mappingType === 'exclude') return 'Excluded'
  if (row.mappingType === 'private') return "Don't import"
  if (row.mappingType === 'spend') return 'Spend factor'
  return row.mappingTypeInput || ''
}

const changeLabel = (row: INominalCodeImportRow): string => {
  if (row.action === 'account') return 'Add to chart'
  return row.action === 'update' ? 'Update' : 'Add'
}

const outcomeDetail = (row: INominalCodeImportRow): string => {
  if (row.mappingType === 'exclude') return row.excludeReason || ''
  if (row.mappingType === 'spend') {
    return [row.spendActivityName, row.groupName].filter(Boolean).join(' · ')
  }
  return row.groupName || ''
}

export const NominalCodeImportDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  onImported: () => void,
}> = ({ open, closeHandler, onImported }) => {
  //Services
  const nominalCodeMappingService = new NominalCodeMappingService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<INominalCodeImportPreview | null>(null)
  const [fileErrorMessage, setFileErrorMessage] = useState<string>('')
  //Loading State
  const [isParsing, setIsParsing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  //Refs
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = () => {
    setFile(null)
    setPreview(null)
    setFileErrorMessage('')
    setIsParsing(false)
    setIsSubmitting(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    resetState()
    closeHandler()
  }

  const downloadTemplateHandler = async () => {
    try {
      setIsDownloading(true)
      const response = await nominalCodeMappingService.getImportTemplate()
      downloadExcel(response, 'Chart of Accounts')
    } catch (err) {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      console.log(err)
    } finally {
      setIsDownloading(false)
    }
  }

  const fileChangeHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    if (!selected) return
    setFile(selected)
    setPreview(null)
    setFileErrorMessage('')
    try {
      setIsParsing(true)
      const response = await nominalCodeMappingService.previewImport(selected)
      setPreview(response)
    } catch (err: any) {
      // The backend writes these messages for the user (no column found, wrong file type, too many
      // rows), so show them rather than a generic failure.
      setFileErrorMessage(err?.response?.data?.message || 'That file could not be read. Please check it is a valid .xlsx or .csv file.')
    } finally {
      setIsParsing(false)
    }
  }

  const submitHandler = async () => {
    if (!preview) return
    const validRows = preview.rows.filter(row => row.isValid)
    if (validRows.length === 0) return
    // Rows with an outcome go to the crosswalk exactly as before; rows with none are added to the
    // chart of accounts only, so the code is listed with no outcome yet.
    const mappingRows = validRows.filter(row => row.action !== 'account' && row.mappingType)
    const accountRows = validRows.filter(row => row.action === 'account')
    const payload: IPostNominalCodeMapping[] = mappingRows.map(row => ({
      nominalCode: row.nominalCode,
      nominalDescription: row.nominalDescription,
      mappingType: row.mappingType!,
      spendBasedConversionId: row.spendBasedConversionId,
      conversionSourceId: row.conversionSourceId,
      excludeReason: row.excludeReason,
      groupId: row.groupId,
    }))
    try {
      setIsSubmitting(true)
      const parts: string[] = []
      let anyFailed = false
      if (payload.length > 0) {
        const response = await nominalCodeMappingService.bulkUpsert(payload)
        // bulkUpsert reports per row, so a row the server refused must not be counted as saved.
        const failed = (response.results as { error?: string }[]).filter(result => result.error)
        if (failed.length > 0) {
          anyFailed = true
          parts.push(`${payload.length - failed.length} of ${payload.length} nominal codes saved. ${failed.length} could not be saved.`)
        } else {
          parts.push(`${payload.length} nominal ${payload.length === 1 ? 'code' : 'codes'} saved.`)
        }
      }
      if (accountRows.length > 0) {
        try {
          await nominalCodeMappingService.importLedgerAccounts(accountRows.map(row => ({
            nominalCode: row.nominalCode,
            nominalDescription: row.nominalDescription,
          })))
          parts.push(`${accountRows.length} ${accountRows.length === 1 ? 'code' : 'codes'} added to your chart with no outcome yet.`)
        } catch (err: any) {
          console.log(err)
          anyFailed = true
          parts.push(err?.response?.data?.message || `${accountRows.length} ${accountRows.length === 1 ? 'code' : 'codes'} could not be added to your chart.`)
        }
      }
      userNotificationsContext.openSnackbar({
        open: true,
        message: parts.join(' '),
        ...(anyFailed ? { backgroundColor: 'text.primary' } : {}),
      })
      onImported()
      handleClose()
    } catch (err) {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      console.log(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const summary = preview?.summary
  const canSubmit = Boolean(summary && summary.valid > 0)
  const submitButtonText = summary && summary.valid > 0
    ? `Save ${summary.valid} nominal ${summary.valid === 1 ? 'code' : 'codes'}`
    : 'Save'

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title='Import Nominal Codes'
      maxWidth='lg'
      cancelHandler={handleClose}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      disableSaveButton={!canSubmit}
      submitButtonText={submitButtonText}
      submitButtonLoadingText='Saving'
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant='body2' color='text.secondary'>
            Map each nominal (GL) code once. Every future spend import reuses the mapping, so you never map a
            transaction row again. A code that already has a mapping is updated; one that does not is created.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            variant='outlined'
            onClick={downloadTemplateHandler}
            disabled={isDownloading}
            startIcon={<DownloadOutlined fontSize='small' />}
          >
            <Typography sx={{ pl: 0.5 }}>{isDownloading ? 'Preparing' : 'Download template'}</Typography>
          </Button>
          <Button
            variant='contained'
            onClick={() => fileInputRef.current?.click()}
            disabled={isParsing}
            startIcon={<FileUploadOutlined fontSize='small' />}
          >
            <Typography sx={{ pl: 0.5 }}>{file ? 'Choose a different file' : 'Choose file'}</Typography>
          </Button>
          {file && (
            <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center' }}>
              {file.name}
            </Typography>
          )}
          <input
            ref={fileInputRef}
            type='file'
            accept={ACCEPTED_FILE_TYPES}
            onChange={fileChangeHandler}
            style={{ display: 'none' }}
          />
        </Stack>

        {fileErrorMessage && (
          <Alert severity='error'>{fileErrorMessage}</Alert>
        )}

        {isParsing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <LoadingSpinner />
          </Box>
        )}

        {!isParsing && preview && summary && (
          <Stack spacing={2}>
            <Stack direction='row' spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Chip label={`${summary.total} ${summary.total === 1 ? 'row' : 'rows'} read`} size='small' variant='outlined' />
              <Chip label={`${summary.toCreate} to add`} size='small' variant='outlined' />
              <Chip label={`${summary.toUpdate} to update`} size='small' variant='outlined' />
              {(summary.toAccount ?? 0) > 0 && (
                <Chip label={`${summary.toAccount} to add to chart`} size='small' variant='outlined' />
              )}
              {summary.invalid > 0 && (
                <Chip label={`${summary.invalid} to fix`} size='small' color='error' variant='outlined' />
              )}
            </Stack>

            <Typography variant='caption' color='text.secondary'>
              Read from the "{preview.sheetName}" sheet, with column headings on row {preview.headerRowNumber}.
            </Typography>

            {preview.unmatchedHeaders.length > 0 && (
              <Alert severity='info'>
                These columns were ignored because they are not part of a nominal code mapping: {preview.unmatchedHeaders.join(', ')}.
              </Alert>
            )}

            {summary.invalid > 0 && (
              <Alert severity='warning'>
                {summary.invalid} {summary.invalid === 1 ? 'row needs' : 'rows need'} attention and will not be saved.
                Fix {summary.invalid === 1 ? 'it' : 'them'} in your file and upload it again, or continue to save the
                {summary.valid === 1 ? ' 1 row' : ` ${summary.valid} rows`} that are ready.
              </Alert>
            )}

            {summary.toUpdate > 0 && (
              <Alert severity='info'>
                {summary.toUpdate} {summary.toUpdate === 1 ? 'code already has a mapping and will be overwritten' : 'codes already have a mapping and will be overwritten'}.
                Existing emissions are not recalculated: the new mapping applies from your next import.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 380, overflowX: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nominal Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Outcome</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map(row => (
                    <TableRow key={row.rowNumber} sx={!row.isValid ? { backgroundColor: theme => alpha(theme.palette.error.main, 0.06) } : undefined}>
                      <TableCell sx={{ color: 'text.secondary' }}>{row.rowNumber}</TableCell>
                      <TableCell>{row.nominalCode || '–'}</TableCell>
                      <TableCell>{row.nominalDescription || '–'}</TableCell>
                      <TableCell>
                        {row.isValid ? (
                          <Stack spacing={0.25}>
                            <Typography variant='body2'>{outcomeLabel(row)}</Typography>
                            {outcomeDetail(row) && (
                              <Typography variant='caption' color='text.secondary'>{outcomeDetail(row)}</Typography>
                            )}
                          </Stack>
                        ) : (
                          <Stack spacing={0.25}>
                            {row.errors.map((error, index) => (
                              <Typography key={index} variant='caption' color='error.main'>{error}</Typography>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.isValid
                          ? <Chip label={changeLabel(row)} size='small' variant='outlined' />
                          : <Typography variant='caption' color='text.secondary'>Skipped</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {!isParsing && !preview && !fileErrorMessage && (
          <Typography variant='body2' color='text.secondary'>
            Choose a file to see what will be added or updated before anything is saved. Your file needs a
            Nominal Code column. A code with no Outcome is added to your chart with no outcome yet, so you
            can give it one on this page later; the template has the rest.
          </Typography>
        )}
      </Stack>
    </DialogWrapper>
  )
}
